import { zodResolver } from '@hookform/resolvers/zod'
import {
	getBilling,
	getUsersMePreferences,
	patchUsersMePreferences,
	postBillingPortalSession,
	type User,
	type UserPreferencesData
} from '@repo/api-portal-sdk/gen'
import { Button } from '@repo/ui/button'
import { Field, FieldDescription, FieldError, FieldLabel } from '@repo/ui/field'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@repo/ui/select'
import { Skeleton } from '@repo/ui/skeleton'
import { Spinner } from '@repo/ui/spinner'
import {
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery
} from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ExternalLink } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import {
	changeAppLanguage,
	resolveAppLanguageCode,
	type TKey,
	useTranslation
} from '@/lib/i18n'
import { portalMeQueryOptions } from '@/lib/portal-session'
import { makeQueryKeys } from '@/lib/query-keys'
import { AccountDeletionDialog } from './-components/account-deletion-dialog'
import { AccountEmailChangeDialog } from './-components/account-email-change-dialog'
import { AccountPassword } from './-components/account-password'
import { AccountSessions } from './-components/account-sessions'
import { AccountTotp } from './-components/account-totp'

const TIME_ZONES = [
	'UTC',
	...Intl.supportedValuesOf('timeZone').filter(
		(timeZone) => timeZone !== 'UTC'
	)
]

function createLanguageSchema(t: (key: TKey) => string) {
	return z.object({
		language: z.enum(['en', 'it'], {
			error: t('common:account.language_invalid')
		})
	})
}

function createTimeZoneSchema(t: (key: TKey) => string) {
	return z.object({
		timeZone: z
			.string()
			.min(1, t('common:account.time_zone_required'))
			.refine(isValidTimeZone, t('common:account.time_zone_invalid'))
	})
}

export const Route = createFileRoute('/(private)/account')({
	ssr: false,
	component: AccountPage
})

function AccountPage() {
	const user = useSuspenseQuery(portalMeQueryOptions()).data
	const { t } = useTranslation()

	return (
		<main className='portal-container py-9 pb-20'>
			<div className='account-page__intro mb-7'>
				<h1>{t('common:account.title')}</h1>
				<p>{t('common:account.subtitle')}</p>
			</div>
			<div className='account-grid grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2'>
				<IdentitySection user={user} />
				<SecuritySection user={user} />
				<PreferencesSection />
				<PlanSection />
				<DangerZone user={user} />
			</div>
		</main>
	)
}

function IdentitySection(props: { user: User }) {
	const { t, i18n } = useTranslation()
	const locale = resolveAppLanguageCode(
		i18n.resolvedLanguage ?? i18n.language
	)
	const emailState = resolveEmailState(props.user, t)

	return (
		<section className='portal-card account-section account-section--identity order-1 min-w-0'>
			<div className='account-section__header mb-5 flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-start'>
				<div>
					<h2>{t('common:account.identity_title')}</h2>
					<p>{t('common:account.identity_description')}</p>
				</div>
				<AccountEmailChangeDialog user={props.user} />
			</div>
			<dl className='account-detail-list grid'>
				<div className='account-detail-row flex min-h-[54px] flex-col items-start justify-between gap-2 border-(--portal-border) border-t py-3.5 first:border-t-0 sm:flex-row sm:items-center sm:gap-[18px] sm:py-0'>
					<dt>{t('common:account.email_label')}</dt>
					<dd className='flex min-w-0 items-center justify-between gap-[9px] sm:justify-end'>
						<span className='account-detail-row__value'>
							{emailState.value}
						</span>
						<span
							className={`account-badge account-badge--${emailState.badgeVariant}`}
						>
							{emailState.label}
						</span>
					</dd>
				</div>
				<div className='account-detail-row flex min-h-[54px] flex-col items-start justify-between gap-2 border-(--portal-border) border-t py-3.5 sm:flex-row sm:items-center sm:gap-[18px] sm:py-0'>
					<dt>{t('common:account.member_since')}</dt>
					<dd className='account-detail-row__value'>
						{new Intl.DateTimeFormat(locale, {
							dateStyle: 'medium'
						}).format(new Date(props.user.createdAt))}
					</dd>
				</div>
			</dl>
		</section>
	)
}

function SecuritySection(props: { user: User }) {
	const { t } = useTranslation()

	return (
		<section className='portal-card account-section account-section--security order-3 min-w-0 sm:col-span-2'>
			<div className='account-section__header mb-5 flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-start'>
				<div>
					<h2>{t('common:account.security_title')}</h2>
					<p>{t('common:account.security_description')}</p>
				</div>
			</div>
			<AccountPassword user={props.user} />
			<AccountTotp user={props.user} />
			<AccountSessions />
		</section>
	)
}

function PreferencesSection() {
	const { t } = useTranslation()
	const preferencesQuery = useQuery({
		queryKey: makeQueryKeys.users.preferences(),
		queryFn: ({ signal }) =>
			getUsersMePreferences({ signal }).then(
				(response) => response.data.data
			)
	})

	return (
		<section className='portal-card account-section account-section--preferences order-4 min-w-0 sm:col-span-2'>
			<div className='account-section__header mb-5 flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-start'>
				<div>
					<h2>{t('common:account.preferences_title')}</h2>
					<p>{t('common:account.preferences_description')}</p>
				</div>
			</div>
			{preferencesQuery.isPending ? (
				<output
					aria-label={t('common:account.preferences_loading')}
					className='account-section-loading grid gap-3.5'
				>
					<Skeleton className='h-20 w-full' />
					<Skeleton className='h-20 w-full' />
				</output>
			) : null}
			{preferencesQuery.isError ? (
				<QueryError
					description={t('common:account.preferences_error')}
					isRetrying={preferencesQuery.isFetching}
					onRetry={() => preferencesQuery.refetch()}
				/>
			) : null}
			{preferencesQuery.isSuccess ? (
				<div className='account-preferences grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-6'>
					<LanguagePreferenceForm
						language={preferencesQuery.data.language}
					/>
					<TimeZonePreferenceForm
						timeZone={preferencesQuery.data.timeZone}
					/>
				</div>
			) : null}
		</section>
	)
}

function LanguagePreferenceForm(props: {
	language: UserPreferencesData['language']
}) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const schema = createLanguageSchema(t)
	const form = useForm<z.infer<ReturnType<typeof createLanguageSchema>>>({
		resolver: zodResolver(schema),
		defaultValues: { language: props.language }
	})
	const updateLanguage = useMutation({
		mutationFn: (input: { language: UserPreferencesData['language'] }) =>
			patchUsersMePreferences({ body: input }).then(
				(response) => response.data.data
			),
		onSuccess: async (preferences) => {
			queryClient.setQueryData(
				makeQueryKeys.users.preferences(),
				preferences
			)
			await changeAppLanguage(preferences.language)
		}
	})

	return (
		<form
			aria-labelledby='account-language-title'
			className='account-preference-form grid min-w-0 gap-4 border-(--portal-border) border-t py-5 first:border-t-0 first:pt-0 last:pb-0 sm:border-t-0 sm:py-0 sm:first:border-r sm:first:pr-6'
			onSubmit={form.handleSubmit((data) => updateLanguage.mutate(data))}
		>
			<div className='account-preference-form__heading flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-start'>
				<div>
					<h3 id='account-language-title'>
						{t('common:account.language_title')}
					</h3>
					<p>{t('common:account.language_description')}</p>
				</div>
				<Button
					className='w-full sm:w-auto'
					disabled={updateLanguage.isPending}
					size='sm'
					type='submit'
				>
					{updateLanguage.isPending ? (
						<Spinner />
					) : (
						t('common:account.save_language')
					)}
				</Button>
			</div>
			<Controller
				control={form.control}
				name='language'
				render={({ field, fieldState }) => (
					<Field data-invalid={fieldState.invalid}>
						<FieldLabel htmlFor='account-language'>
							{t('common:account.language_label')}
						</FieldLabel>
						<Select
							onValueChange={(value) => {
								if (value === 'en' || value === 'it') {
									field.onChange(value)
								}
							}}
							value={field.value}
						>
							<SelectTrigger
								aria-invalid={fieldState.invalid}
								className='account-select w-full'
								id='account-language'
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='en'>English</SelectItem>
								<SelectItem value='it'>Italiano</SelectItem>
							</SelectContent>
						</Select>
						{fieldState.error ? (
							<FieldError errors={[fieldState.error]} />
						) : null}
					</Field>
				)}
			/>
			<MutationMessage
				isError={updateLanguage.isError}
				isSuccess={updateLanguage.isSuccess}
				success={t('common:account.language_saved')}
			/>
		</form>
	)
}

function TimeZonePreferenceForm(props: { timeZone: string }) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const schema = createTimeZoneSchema(t)
	const form = useForm<z.infer<ReturnType<typeof createTimeZoneSchema>>>({
		resolver: zodResolver(schema),
		defaultValues: { timeZone: props.timeZone }
	})
	const updateTimeZone = useMutation({
		mutationFn: (input: { timeZone: string }) =>
			patchUsersMePreferences({ body: input }).then(
				(response) => response.data.data
			),
		onSuccess: (preferences) => {
			queryClient.setQueryData(
				makeQueryKeys.users.preferences(),
				preferences
			)
		}
	})
	const timeZones = TIME_ZONES.includes(props.timeZone)
		? TIME_ZONES
		: [props.timeZone, ...TIME_ZONES]

	return (
		<form
			aria-labelledby='account-time-zone-title'
			className='account-preference-form grid min-w-0 gap-4 border-(--portal-border) border-t py-5 first:border-t-0 first:pt-0 last:pb-0 sm:border-t-0 sm:py-0 sm:first:border-r sm:first:pr-6'
			onSubmit={form.handleSubmit((data) => updateTimeZone.mutate(data))}
		>
			<div className='account-preference-form__heading flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-start'>
				<div>
					<h3 id='account-time-zone-title'>
						{t('common:account.time_zone_title')}
					</h3>
					<p>{t('common:account.time_zone_description')}</p>
				</div>
				<Button
					className='w-full sm:w-auto'
					disabled={updateTimeZone.isPending}
					size='sm'
					type='submit'
				>
					{updateTimeZone.isPending ? (
						<Spinner />
					) : (
						t('common:account.save_time_zone')
					)}
				</Button>
			</div>
			<Controller
				control={form.control}
				name='timeZone'
				render={({ field, fieldState }) => (
					<Field data-invalid={fieldState.invalid}>
						<FieldLabel htmlFor='account-time-zone'>
							{t('common:account.time_zone_label')}
						</FieldLabel>
						<Select
							onValueChange={(value) =>
								field.onChange(value ?? '')
							}
							value={field.value}
						>
							<SelectTrigger
								aria-invalid={fieldState.invalid}
								className='account-select w-full'
								id='account-time-zone'
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{timeZones.map((timeZone) => (
									<SelectItem key={timeZone} value={timeZone}>
										{timeZone}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<FieldDescription>
							{t('common:account.time_zone_hint')}
						</FieldDescription>
						{fieldState.error ? (
							<FieldError errors={[fieldState.error]} />
						) : null}
					</Field>
				)}
			/>
			<MutationMessage
				isError={updateTimeZone.isError}
				isSuccess={updateTimeZone.isSuccess}
				success={t('common:account.time_zone_saved')}
			/>
		</form>
	)
}

function MutationMessage(props: {
	isError: boolean
	isSuccess: boolean
	success: string
}) {
	const { t } = useTranslation()
	if (props.isError) {
		return (
			<p
				className='account-form-message account-form-message--error'
				role='alert'
			>
				{t('common:account.preferences_save_error')}
			</p>
		)
	}
	if (props.isSuccess) {
		return <output className='account-form-message'>{props.success}</output>
	}
	return null
}

function PlanSection() {
	const { t, i18n } = useTranslation()
	const locale = resolveAppLanguageCode(
		i18n.resolvedLanguage ?? i18n.language
	)
	const billingQuery = useQuery({
		queryKey: makeQueryKeys.billing.subscription(),
		queryFn: ({ signal }) =>
			getBilling({ signal }).then((response) => response.data.data)
	})
	const openPortal = useMutation({
		mutationFn: () => postBillingPortalSession(),
		onSuccess: (response) => window.location.assign(response.data.data.url)
	})

	return (
		<section className='portal-card account-section account-section--plan order-2 min-w-0 sm:col-start-2 sm:row-start-1'>
			<div className='account-section__header mb-5 flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-start'>
				<div>
					<h2>{t('common:account.plan_title')}</h2>
					<p>{t('common:account.plan_description')}</p>
				</div>
			</div>
			{billingQuery.isPending ? (
				<output
					aria-label={t('common:account.plan_loading')}
					className='account-section-loading grid gap-3.5'
				>
					<Skeleton className='h-20 w-full' />
				</output>
			) : null}
			{billingQuery.isError ? (
				<QueryError
					description={t('common:account.plan_error')}
					isRetrying={billingQuery.isFetching}
					onRetry={() => billingQuery.refetch()}
				/>
			) : null}
			{billingQuery.isSuccess && billingQuery.data === null ? (
				<div className='account-plan-summary flex w-full items-start justify-between gap-4'>
					<div>
						<strong>{t('common:account.free_plan')}</strong>
						<p>{t('common:account.free_plan_description')}</p>
					</div>
					<span className='account-badge account-badge--secondary'>
						{t('common:account.plan_current')}
					</span>
				</div>
			) : null}
			{billingQuery.isSuccess && billingQuery.data !== null ? (
				<div className='account-plan-details grid justify-items-start gap-[18px]'>
					<div className='account-plan-summary flex w-full items-start justify-between gap-4'>
						<div>
							<strong>
								{formatPlanCode(billingQuery.data.planCode)}
							</strong>
							<p>
								{resolveBillingPeriodLabel(
									billingQuery.data.currentPeriodEnd,
									billingQuery.data.cancelAtPeriodEnd,
									locale,
									t
								)}
							</p>
						</div>
						<span className='account-badge account-badge--secondary'>
							{resolveBillingStatus(billingQuery.data.status, t)}
						</span>
					</div>
					<Button
						disabled={openPortal.isPending}
						onClick={() => openPortal.mutate()}
						variant='outline'
					>
						{openPortal.isPending ? <Spinner /> : <ExternalLink />}
						{t('common:account.manage_billing')}
					</Button>
					{openPortal.isError ? (
						<p
							className='account-form-message account-form-message--error'
							role='alert'
						>
							{t('common:account.billing_portal_error')}
						</p>
					) : null}
				</div>
			) : null}
		</section>
	)
}

function QueryError(props: {
	description: string
	isRetrying: boolean
	onRetry: () => void
}) {
	const { t } = useTranslation()
	return (
		<div
			className='account-query-error grid min-h-[126px] content-center justify-items-start gap-3'
			role='alert'
		>
			<p>{props.description}</p>
			<Button
				disabled={props.isRetrying}
				onClick={props.onRetry}
				size='sm'
				variant='outline'
			>
				{retryLabel(props.isRetrying, t)}
			</Button>
		</div>
	)
}

function DangerZone(props: { user: User }) {
	const { t } = useTranslation()
	return (
		<section className='portal-card account-section account-section--danger order-5 min-w-0 sm:col-span-2'>
			<div className='account-section__header mb-5 flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-start'>
				<div>
					<h2>{t('common:account.danger_title')}</h2>
					<p>{t('common:account.danger_description')}</p>
				</div>
			</div>
			<div className='account-danger-content flex flex-col items-stretch justify-between gap-6 sm:flex-row sm:items-end'>
				<ul className='account-danger-consequences grid'>
					<li>{t('common:account.danger_irreversible')}</li>
					<li>{t('common:account.danger_revoke_access')}</li>
					<li>{t('common:account.danger_stop_renewal')}</li>
					<li>{t('common:account.danger_no_refund')}</li>
				</ul>
				<AccountDeletionDialog user={props.user} />
			</div>
		</section>
	)
}

function resolveEmailState(user: User, t: (key: TKey) => string) {
	if (user.emailStatus === 'verified' && user.email) {
		return {
			badgeVariant: 'secondary' as const,
			label: t('common:account.email_verified'),
			value: user.email
		}
	}
	if (user.emailStatus === 'unverified') {
		return {
			badgeVariant: 'outline' as const,
			label: t('common:account.email_unverified'),
			value: t('common:account.email_unverified_value')
		}
	}
	return {
		badgeVariant: 'outline' as const,
		label: t('common:account.email_missing'),
		value: t('common:account.email_missing_value')
	}
}

function resolveBillingStatus(status: string, t: (key: TKey) => string) {
	switch (status) {
		case 'active':
			return t('common:account.billing_status_active')
		case 'trialing':
			return t('common:account.billing_status_trialing')
		case 'past_due':
			return t('common:account.billing_status_past_due')
		case 'canceled':
			return t('common:account.billing_status_canceled')
		default:
			return t('common:account.billing_status_synchronized')
	}
}

function resolveBillingPeriodLabel(
	currentPeriodEnd: string | null,
	cancelAtPeriodEnd: boolean,
	locale: string,
	t: (key: TKey, options?: { date: string }) => string
) {
	if (!currentPeriodEnd) {
		return t('common:account.billing_period_unavailable')
	}
	const date = new Intl.DateTimeFormat(locale, {
		dateStyle: 'medium'
	}).format(new Date(currentPeriodEnd))
	return cancelAtPeriodEnd
		? t('common:account.billing_access_until', { date })
		: t('common:account.billing_renews', { date })
}

function retryLabel(isRetrying: boolean, t: (key: TKey) => string) {
	return isRetrying ? t('common:account.retrying') : t('common:account.retry')
}

function formatPlanCode(planCode: string) {
	const words = planCode.replaceAll(/[-_]/g, ' ')
	return words.charAt(0).toUpperCase() + words.slice(1)
}

function isValidTimeZone(timeZone: string) {
	try {
		new Intl.DateTimeFormat('en', { timeZone }).format()
		return true
	} catch {
		return false
	}
}
